<template>
  <div class="user-detail-overlay" @click="closeModal">
    <div class="user-detail-modal" @click.stop>
      <div class="modal-header">
        <h2>User Details</h2>
        <div class="header-actions">
          <button
            v-if="activeTab === 'details' && !isEditing"
            class="edit-btn"
            @click="startEditing"
          >
            Edit
          </button>
          <button
            v-if="activeTab === 'details' && isEditing"
            :disabled="isSaving"
            class="save-btn"
            @click="saveChanges"
          >
            {{ isSaving ? 'Saving...' : 'Save' }}
          </button>
          <button v-if="isEditing" class="cancel-btn" @click="cancelEditing">Cancel</button>
          <button class="close-btn" @click="closeModal">&times;</button>
        </div>
      </div>

      <div class="modal-tabs">
        <button
          :class="['tab-btn', { active: activeTab === 'details' }]"
          @click="activeTab = 'details'"
        >
          Details
        </button>
        <button :class="['tab-btn', { active: activeTab === 'emails' }]" @click="selectEmailsTab">
          Emails
        </button>
        <button
          :class="['tab-btn', { active: activeTab === 'bugReports' }]"
          @click="selectBugReportsTab"
        >
          Bug reports
        </button>
      </div>

      <div class="modal-content">
        <div v-show="activeTab === 'details'" class="user-info-grid">
          <!-- Basic Info Section -->
          <div class="info-section">
            <h3>Basic Information</h3>
            <div class="info-row">
              <span class="label">ID:</span>
              <span class="value">{{ user.id }}</span>
            </div>
            <div class="info-row">
              <span class="label">Username:</span>
              <input v-if="isEditing" v-model="editForm.username" type="text" class="edit-input" />
              <span v-else class="value">{{ user.username }}</span>
            </div>
            <div class="info-row">
              <span class="label">Email:</span>
              <input v-if="isEditing" v-model="editForm.email" type="email" class="edit-input" />
              <span v-else class="value">{{ user.email }}</span>
            </div>
            <div class="info-row">
              <span class="label">Email Verified:</span>
              <div v-if="isEditing" class="email-verification-edit">
                <input
                  v-if="!user.emailVerified"
                  v-model="editForm.emailVerified"
                  type="checkbox"
                  class="edit-checkbox"
                />
                <span v-else class="verified-locked">
                  <span class="badge badge-verified">Verified</span>
                  <span class="lock-text">(Cannot unverify)</span>
                </span>
              </div>
              <span
                v-else
                :class="['badge', user.emailVerified ? 'badge-verified' : 'badge-unverified']"
              >
                {{ user.emailVerified ? 'Verified' : 'Not Verified' }}
              </span>
            </div>
            <div class="info-row">
              <span class="label">Created:</span>
              <span class="value">{{ formatDateTime(user.createdAt) }}</span>
            </div>
            <div v-if="user.deletedAt" class="info-row">
              <span class="label">Deleted:</span>
              <span class="value">{{ formatDateTime(user.deletedAt) }}</span>
            </div>
          </div>

          <!-- Status Section -->
          <div class="info-section">
            <h3>Status & Permissions</h3>
            <div class="info-row">
              <span class="label">Admin:</span>
              <span :class="['badge', user.isAdmin ? 'badge-admin' : 'badge-regular']">
                {{ user.isAdmin ? 'Yes' : 'No' }}
              </span>
            </div>
            <div class="info-row">
              <span class="label">Supporter:</span>
              <input
                v-if="isEditing"
                v-model="editForm.isSupporter"
                type="checkbox"
                class="edit-checkbox"
              />
              <span
                v-else
                :class="['badge', user.isSupporter ? 'badge-supporter' : 'badge-regular']"
              >
                {{ user.isSupporter ? 'Yes' : 'No' }}
              </span>
            </div>
            <div class="info-row">
              <span class="label">Studio access:</span>
              <input
                v-if="isEditing"
                v-model="editForm.studioAccess"
                type="checkbox"
                class="edit-checkbox"
              />
              <span
                v-else
                :class="['badge', user.studioAccess ? 'badge-supporter' : 'badge-regular']"
              >
                {{ user.studioAccess ? 'Yes' : 'No' }}
              </span>
            </div>
            <div class="info-row">
              <span class="label">Bug reports blocked:</span>
              <input
                v-if="isEditing"
                v-model="editForm.bugReportsBlocked"
                type="checkbox"
                class="edit-checkbox"
              />
              <span
                v-else
                :class="['badge', user.bugReportsBlocked ? 'badge-deleted' : 'badge-regular']"
              >
                {{ user.bugReportsBlocked ? 'Yes' : 'No' }}
              </span>
            </div>
            <div class="info-row">
              <span class="label">Status:</span>
              <div v-if="isEditing" class="edit-select-container">
                <label class="checkbox-label">
                  <input v-model="editForm.deleted" type="checkbox" class="edit-checkbox" />
                  Deleted
                </label>
              </div>
              <span v-else :class="['badge', user.deleted ? 'badge-deleted' : 'badge-active']">
                {{ user.deleted ? 'Deleted' : 'Active' }}
              </span>
            </div>
            <div class="info-row">
              <span class="label">Premium Until:</span>
              <div v-if="isEditing" class="premium-date-container">
                <input
                  v-model="editForm.premiumUntil"
                  type="datetime-local"
                  class="edit-input date-input"
                />
                <div class="date-actions">
                  <button type="button" class="date-action-btn clear-btn" @click="clearPremiumDate">
                    Clear
                  </button>
                  <button
                    type="button"
                    class="date-action-btn preset-btn"
                    @click="setOneMonthFromNow"
                  >
                    +1 Month
                  </button>
                  <button
                    type="button"
                    class="date-action-btn preset-btn"
                    @click="setThreeMonthsFromNow"
                  >
                    +3 Months
                  </button>
                </div>
              </div>
              <span v-else-if="user.premiumUntil" class="value">{{
                formatDateTime(user.premiumUntil)
              }}</span>
              <span v-else class="value">Not set</span>
            </div>
          </div>

          <!-- Activity Section -->
          <div class="info-section">
            <h3>Activity</h3>
            <div class="info-row">
              <span class="label">Lists:</span>
              <span class="value activity-count">{{ user.listCount }}</span>
            </div>
            <div class="info-row">
              <span class="label">Games:</span>
              <span class="value activity-count">{{ user.gameCount }}</span>
            </div>
          </div>

          <!-- Subscription Section -->
          <div class="info-section">
            <h3>Subscription</h3>
            <div v-if="user.activeSubscription" class="subscription-info">
              <div class="info-row">
                <span class="label">Product:</span>
                <span class="value">{{ user.activeSubscription.productName }}</span>
              </div>
              <div class="info-row">
                <span class="label">Status:</span>
                <span :class="['badge', getBadgeClass(user.activeSubscription)]">
                  {{ user.activeSubscription.isGifted ? 'Gifted' : 'Premium' }}
                </span>
              </div>
              <div class="info-row">
                <span class="label">Expires:</span>
                <span class="value">{{
                  formatDateTime(user.activeSubscription.currentPeriodEnd)
                }}</span>
              </div>
              <div class="info-row">
                <span class="label">Type:</span>
                <span class="value">{{
                  user.activeSubscription.isGifted ? 'Gifted Premium' : 'Paid Subscription'
                }}</span>
              </div>
              <div v-if="!user.activeSubscription.isGifted" class="info-row">
                <span class="label">Merchant of record:</span>
                <span class="value">
                  {{
                    user.activeSubscription.managedPayments
                      ? 'Link (Managed Payments)'
                      : 'Us (legacy Stripe)'
                  }}
                  <span v-if="user.activeSubscription.managedPayments" class="badge badge-mor"
                    >MoR</span
                  >
                </span>
              </div>
              <div v-if="user.stripeCustomerId" class="info-row">
                <span class="label">Stripe Customer ID:</span>
                <span class="value technical-id">{{ user.stripeCustomerId }}</span>
              </div>
            </div>
            <div v-else class="no-subscription">
              <span class="badge badge-free">No Active Subscription</span>
            </div>
          </div>

          <!-- Admin Notes Section -->
          <div class="info-section">
            <h3>Admin Notes</h3>
            <div class="info-row notes-row">
              <textarea
                v-if="isEditing"
                v-model="editForm.adminNotes"
                placeholder="Add admin notes about this user..."
                class="edit-textarea"
                rows="4"
              ></textarea>
              <div v-else class="notes-display">
                <p v-if="user.adminNotes" class="notes-content">{{ user.adminNotes }}</p>
                <p v-else class="no-notes">No admin notes</p>
              </div>
            </div>
          </div>

          <!-- Awards Section -->
          <div class="info-section">
            <h3>Awards</h3>
            <div class="awards-container">
              <div v-if="isEditing" class="awards-edit">
                <div v-for="(award, index) in editForm.awards" :key="index" class="award-edit-row">
                  <input
                    v-model="award.icon"
                    type="text"
                    placeholder="fa-icon-name"
                    class="edit-input award-icon-input"
                  />
                  <input
                    v-model="award.description"
                    type="text"
                    placeholder="Award description"
                    class="edit-input award-desc-input"
                  />
                  <button
                    type="button"
                    class="remove-award-btn"
                    title="Remove award"
                    @click="removeAward(index)"
                  >
                    &times;
                  </button>
                </div>
                <button type="button" class="add-award-btn" @click="addAward">+ Add Award</button>
              </div>
              <div v-else class="awards-display">
                <div v-if="user.awards && user.awards.length > 0" class="awards-list">
                  <div v-for="(award, index) in parsedAwards" :key="index" class="award-item">
                    <i :class="`fa-solid ${award.icon}`" class="award-icon"></i>
                    <span class="award-description">{{ award.description }}</span>
                  </div>
                </div>
                <p v-else class="no-awards">No awards</p>
              </div>
            </div>
          </div>

          <!-- Account Actions Section -->
          <div v-if="user.loginMethod !== 'discord'" class="info-section">
            <h3>Account Actions</h3>
            <div class="action-content">
              <div class="action-description">
                <h4>Password Reset</h4>
                <p>
                  Email <strong>{{ user.email }}</strong> a single-use link to set a new password.
                  The link is valid for 1 hour.
                </p>
              </div>
              <button
                class="action-btn"
                :disabled="isEditing || isResettingPassword"
                @click="showResetConfirmation = true"
              >
                {{ isResettingPassword ? 'Sending...' : 'Send Password Reset' }}
              </button>
              <div v-if="resetResult" class="reset-result">
                <p :class="resetResult.emailSent ? 'reset-success' : 'reset-warning'">
                  {{ resetResult.message }}
                </p>
                <label class="reset-link-label">
                  Reset link (share directly if needed):
                  <input :value="resetResult.resetLink" readonly class="reset-link-input" />
                </label>
              </div>
            </div>
          </div>

          <!-- Danger Zone Section (only show for deleted users) -->
          <div v-if="user.deleted" class="info-section danger-zone">
            <h3>Danger Zone</h3>
            <div class="danger-content">
              <div class="danger-description">
                <h4>Permanent Deletion</h4>
                <p>
                  This user is marked as deleted. You can permanently remove all user data from the
                  database. This action cannot be undone.
                </p>
              </div>
              <button
                class="danger-btn"
                :disabled="isEditing"
                @click="showDeleteConfirmation = true"
              >
                Permanently Delete User
              </button>
            </div>
          </div>
        </div>

        <!-- Emails Tab -->
        <div v-show="activeTab === 'emails'" class="emails-panel">
          <div v-if="emailsLoading" class="emails-state">Loading emails…</div>
          <div v-else-if="emailsError" class="emails-state emails-error">{{ emailsError }}</div>
          <div v-else-if="userEmails.length === 0" class="emails-state">
            No emails found for {{ user.email }}.
          </div>
          <table v-else class="emails-table">
            <thead>
              <tr>
                <th>Subject</th>
                <th>Sent</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(email, index) in userEmails" :key="email.emailReference || index">
                <td class="email-subject">
                  {{ email.subject }}
                  <span v-if="email.templateName" class="email-template">{{
                    email.templateName
                  }}</span>
                </td>
                <td class="email-date">{{ formatEmailDate(email.sentAt) }}</td>
                <td>
                  <span :class="['email-badge', emailStatusClass(email.status)]">
                    {{ emailStatusLabel(email.status) }}
                  </span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Bug Reports Tab -->
        <div v-show="activeTab === 'bugReports'" class="emails-panel">
          <div v-if="bugReportsLoading" class="emails-state">Loading bug reports…</div>
          <div v-else-if="bugReportsError" class="emails-state emails-error">
            {{ bugReportsError }}
          </div>
          <div v-else-if="userBugReports.length === 0" class="emails-state">
            No bug reports submitted by {{ user.username }}.
          </div>
          <table v-else class="emails-table">
            <thead>
              <tr>
                <th>Report</th>
                <th>Submitted</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="report in userBugReports"
                :key="report.id"
                class="bug-report-row"
                @click="selectedBugReport = report"
              >
                <td class="email-subject">
                  {{ report.name }}
                  <span class="email-template">{{ report.text }}</span>
                </td>
                <td class="email-date">{{ report.createdAt.toLocaleString() }}</td>
              </tr>
            </tbody>
          </table>
        </div>

        <Teleport to="body">
          <BugReportDetail
            v-if="selectedBugReport"
            :report="selectedBugReport"
            @close="selectedBugReport = null"
          />
        </Teleport>

        <!-- Password Reset Confirmation Modal -->
        <div
          v-if="showResetConfirmation"
          class="delete-confirmation-overlay"
          @click="showResetConfirmation = false"
        >
          <div class="delete-confirmation-modal" @click.stop>
            <h3 class="reset-modal-title">Send Password Reset</h3>
            <p>
              Send a password reset link to <strong>{{ user.email }}</strong> for
              <strong>{{ user.username }}</strong
              >?
            </p>
            <p class="reset-modal-note">
              This emails the user a single-use link to set a new password. It does not change their
              password directly.
            </p>

            <div class="delete-confirmation-actions">
              <button class="cancel-delete-btn" @click="showResetConfirmation = false">
                Cancel
              </button>
              <button
                :disabled="isResettingPassword"
                class="confirm-reset-btn"
                @click="resetPassword"
              >
                {{ isResettingPassword ? 'Sending...' : 'Send Reset' }}
              </button>
            </div>
          </div>
        </div>

        <!-- Delete Confirmation Modal -->
        <div
          v-if="showDeleteConfirmation"
          class="delete-confirmation-overlay"
          @click="showDeleteConfirmation = false"
        >
          <div class="delete-confirmation-modal" @click.stop>
            <h3>Confirm Permanent Deletion</h3>
            <p>
              Are you sure you want to permanently delete <strong>{{ user.username }}</strong
              >?
            </p>
            <p class="warning-text">
              This will remove all user data including lists, games, and settings. This action
              cannot be undone.
            </p>

            <div class="delete-confirmation-actions">
              <button class="cancel-delete-btn" @click="showDeleteConfirmation = false">
                Cancel
              </button>
              <button
                :disabled="isDeleting"
                class="confirm-delete-btn"
                @click="permanentlyDeleteUser"
              >
                {{ isDeleting ? 'Deleting...' : 'Permanently Delete' }}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, computed } from 'vue';
import type { AdminUser, ActiveSubscription, BugReport, UserEmailEntry } from '../client.js';
import { client } from '../client.js';
import BugReportDetail from './BugReportDetail.vue';

interface Props {
  user: AdminUser;
}

const props = defineProps<Props>();

const emit = defineEmits<{
  close: [];
  userUpdated: [user: AdminUser];
  userDeleted: [userId: string];
}>();

interface AwardEdit {
  icon: string;
  description: string;
}

const isEditing = ref(false);
const isSaving = ref(false);
const showDeleteConfirmation = ref(false);
const isDeleting = ref(false);
const showResetConfirmation = ref(false);
const isResettingPassword = ref(false);
const resetResult = ref<{ message: string; emailSent: boolean; resetLink: string } | null>(null);

const activeTab = ref<'details' | 'emails' | 'bugReports'>('details');
const userEmails = ref<UserEmailEntry[]>([]);
const emailsLoading = ref(false);
const emailsError = ref<string | null>(null);
const emailsLoaded = ref(false);

const selectEmailsTab = () => {
  activeTab.value = 'emails';
  if (!emailsLoaded.value) loadEmails();
};

const loadEmails = async () => {
  if (!client.value) return;
  emailsLoading.value = true;
  emailsError.value = null;
  try {
    const emails = await client.value.getUserEmails(props.user.id);
    if (emails === null) {
      emailsError.value = 'Email reporting is not configured on the server.';
    } else {
      userEmails.value = emails;
    }
    emailsLoaded.value = true;
  } catch (error: any) {
    emailsError.value = error.message || 'Failed to load emails';
  } finally {
    emailsLoading.value = false;
  }
};

const userBugReports = ref<BugReport[]>([]);
const bugReportsLoading = ref(false);
const bugReportsError = ref<string | null>(null);
const bugReportsLoaded = ref(false);
const selectedBugReport = ref<BugReport | null>(null);

const selectBugReportsTab = () => {
  activeTab.value = 'bugReports';
  if (!bugReportsLoaded.value) loadBugReports();
};

const loadBugReports = async () => {
  if (!client.value) return;
  bugReportsLoading.value = true;
  bugReportsError.value = null;
  try {
    userBugReports.value = await client.value.getBugReports(props.user.id);
    bugReportsLoaded.value = true;
  } catch (error: any) {
    bugReportsError.value = error.message || 'Failed to load bug reports';
  } finally {
    bugReportsLoading.value = false;
  }
};

const formatEmailDate = (iso: string | null) => (iso ? new Date(iso).toLocaleString() : '—');

const emailStatusLabel = (status: UserEmailEntry['status']) =>
  ({
    delivered: 'Delivered',
    hard_bounce: 'Hard Bounce',
    soft_bounce: 'Soft Bounce',
    sent: 'Sent',
  })[status];

const emailStatusClass = (status: UserEmailEntry['status']) =>
  ({
    delivered: 'badge-delivered',
    hard_bounce: 'badge-hard-bounce',
    soft_bounce: 'badge-soft-bounce',
    sent: 'badge-sent',
  })[status];

// Parse awards from string array to object array
function parseAwardsForEdit(awards: string[]): AwardEdit[] {
  return awards.map((award) => {
    const [icon, ...descParts] = award.split(';');
    return {
      icon: icon || '',
      description: descParts.join(';') || '',
    };
  });
}

// Parse awards for display
const parsedAwards = computed(() => {
  if (!props.user.awards || props.user.awards.length === 0) return [];
  return props.user.awards.map((award) => {
    const [icon, ...descParts] = award.split(';');
    return {
      icon: icon || '',
      description: descParts.join(';') || '',
    };
  });
});

const editForm = reactive({
  username: props.user.username,
  email: props.user.email,
  emailVerified: props.user.emailVerified,
  isSupporter: props.user.isSupporter,
  studioAccess: props.user.studioAccess,
  bugReportsBlocked: props.user.bugReportsBlocked,
  deleted: props.user.deleted,
  premiumUntil: props.user.premiumUntil ? formatDateForInput(props.user.premiumUntil) : '',
  adminNotes: props.user.adminNotes,
  awards: parseAwardsForEdit(props.user.awards || []),
});

function formatDateForInput(date: Date): string {
  // Format date for datetime-local input (YYYY-MM-DDTHH:mm)
  const d = new Date(date);
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  const hours = String(d.getHours()).padStart(2, '0');
  const minutes = String(d.getMinutes()).padStart(2, '0');
  return `${year}-${month}-${day}T${hours}:${minutes}`;
}

const closeModal = () => {
  emit('close');
};

const startEditing = () => {
  // Reset form to current user values
  editForm.username = props.user.username;
  editForm.email = props.user.email;
  // Only allow setting emailVerified to true if it's currently false
  editForm.emailVerified = props.user.emailVerified;
  editForm.isSupporter = props.user.isSupporter;
  editForm.studioAccess = props.user.studioAccess;
  editForm.bugReportsBlocked = props.user.bugReportsBlocked;
  editForm.deleted = props.user.deleted;
  editForm.premiumUntil = props.user.premiumUntil
    ? formatDateForInput(props.user.premiumUntil)
    : '';
  editForm.adminNotes = props.user.adminNotes;
  editForm.awards = parseAwardsForEdit(props.user.awards || []);
  isEditing.value = true;
};

const addAward = () => {
  editForm.awards.push({ icon: '', description: '' });
};

const removeAward = (index: number) => {
  editForm.awards.splice(index, 1);
};

const cancelEditing = () => {
  isEditing.value = false;
};

const saveChanges = async () => {
  if (!client.value) return;

  isSaving.value = true;

  try {
    // Convert awards back to string array
    const awardsStringArray = editForm.awards
      .filter((award) => award.icon.trim() || award.description.trim()) // Filter out empty awards
      .map((award) => `${award.icon};${award.description}`);

    const updates: any = {
      username: editForm.username,
      email: editForm.email,
      isSupporter: editForm.isSupporter,
      studioAccess: editForm.studioAccess,
      bugReportsBlocked: editForm.bugReportsBlocked,
      deleted: editForm.deleted,
      premiumUntil: editForm.premiumUntil ? new Date(editForm.premiumUntil) : null,
      adminNotes: editForm.adminNotes,
      awards: awardsStringArray,
    };

    // Only include emailVerified if it's being changed from false to true
    if (!props.user.emailVerified && editForm.emailVerified) {
      updates.emailVerified = editForm.emailVerified;
    }

    const updatedUser = await client.value.updateUser(props.user.id, updates);
    emit('userUpdated', updatedUser);
    isEditing.value = false;

    window.alert?.('User updated successfully!');
  } catch (error: any) {
    console.error('Error updating user:', error);
    window.alert?.(`Failed to update user: ${error.message}`);
  } finally {
    isSaving.value = false;
  }
};

const formatDateTime = (date: Date) => {
  return date.toLocaleString();
};

const getBadgeClass = (subscription: ActiveSubscription) => {
  return subscription.isGifted ? 'badge-gifted' : 'badge-premium';
};

const clearPremiumDate = () => {
  editForm.premiumUntil = '';
};

const setOneMonthFromNow = () => {
  const oneMonthFromNow = new Date();
  oneMonthFromNow.setMonth(oneMonthFromNow.getMonth() + 1);
  editForm.premiumUntil = formatDateForInput(oneMonthFromNow);
};

const setThreeMonthsFromNow = () => {
  const threeMonthsFromNow = new Date();
  threeMonthsFromNow.setMonth(threeMonthsFromNow.getMonth() + 3);
  editForm.premiumUntil = formatDateForInput(threeMonthsFromNow);
};

const resetPassword = async () => {
  if (!client.value) return;

  isResettingPassword.value = true;

  try {
    const result = await client.value.resetUserPassword(props.user.id);
    resetResult.value = {
      message: result.message,
      emailSent: result.emailSent,
      resetLink: result.resetLink,
    };
    showResetConfirmation.value = false;
  } catch (error: any) {
    console.error('Error issuing password reset:', error);
    window.alert?.(`Failed to issue password reset: ${error.message}`);
  } finally {
    isResettingPassword.value = false;
  }
};

const permanentlyDeleteUser = async () => {
  if (!client.value) return;

  isDeleting.value = true;

  try {
    await client.value.deleteUser(props.user.id);
    showDeleteConfirmation.value = false;
    emit('userDeleted', props.user.id);
    emit('close');

    window.alert?.('User permanently deleted successfully!');
  } catch (error: any) {
    console.error('Error deleting user:', error);
    window.alert?.(`Failed to delete user: ${error.message}`);
  } finally {
    isDeleting.value = false;
  }
};
</script>

<style scoped>
.user-detail-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: 1rem;
}

.user-detail-modal {
  background: white;
  border-radius: 0.5rem;
  box-shadow:
    0 20px 25px -5px rgba(0, 0, 0, 0.1),
    0 10px 10px -5px rgba(0, 0, 0, 0.04);
  max-width: 800px;
  width: 100%;
  max-height: 90vh;
  display: flex;
  flex-direction: column;
}

.modal-header {
  padding: 1.5rem;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-shrink: 0;
}

.header-actions {
  display: flex;
  gap: 0.5rem;
  align-items: center;
}

.edit-btn,
.save-btn,
.cancel-btn {
  padding: 0.5rem 1rem;
  border: none;
  border-radius: 0.375rem;
  cursor: pointer;
  font-size: 0.875rem;
  font-weight: 500;
  transition: background-color 0.2s;
}

.edit-btn {
  background: #3b82f6;
  color: white;
}

.edit-btn:hover {
  background: #2563eb;
}

.save-btn {
  background: #10b981;
  color: white;
}

.save-btn:hover:not(:disabled) {
  background: #059669;
}

.save-btn:disabled {
  background: #9ca3af;
  cursor: not-allowed;
}

.cancel-btn {
  background: #6b7280;
  color: white;
}

.cancel-btn:hover {
  background: #4b5563;
}

.modal-header h2 {
  margin: 0;
  color: #1f2937;
  font-size: 1.5rem;
}

.close-btn {
  background: none;
  border: none;
  font-size: 2rem;
  color: #6b7280;
  cursor: pointer;
  line-height: 1;
  padding: 0;
  width: 2rem;
  height: 2rem;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 0.25rem;
}

.close-btn:hover {
  background: #f3f4f6;
  color: #374151;
}

.modal-content {
  padding: 1.5rem;
  overflow-y: auto;
  flex: 1;
}

.user-info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1.5rem;
}

.info-section {
  background: #f9fafb;
  padding: 1.25rem;
  border-radius: 0.5rem;
  border: 1px solid #e5e7eb;
}

.info-section h3 {
  margin: 0 0 1rem 0;
  color: #374151;
  font-size: 1.125rem;
  font-weight: 600;
  border-bottom: 2px solid #e5e7eb;
  padding-bottom: 0.5rem;
}

.info-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 0;
  border-bottom: 1px solid #e5e7eb;
}

.info-row:last-child {
  border-bottom: none;
}

.label {
  font-weight: 500;
  color: #374151;
  font-size: 0.875rem;
}

.value {
  color: #1f2937;
  font-family: 'Courier New', monospace;
  word-break: break-all;
}

.technical-id {
  font-size: 0.75rem;
  color: #6b7280;
}

.activity-count {
  font-weight: 600;
  color: #3b82f6;
  font-size: 1.125rem;
}

.badge {
  padding: 0.25rem 0.75rem;
  border-radius: 9999px;
  font-size: 0.75rem;
  font-weight: 500;
  text-transform: uppercase;
  white-space: nowrap;
}

.badge-admin {
  background: #fef3c7;
  color: #92400e;
}

.badge-supporter {
  background: #e0e7ff;
  color: #3730a3;
}

.badge-premium {
  background: #d1fae5;
  color: #065f46;
}

/* Managed Payments (Link is merchant of record) */
.badge-mor {
  background: #e0e7ff;
  color: #3730a3;
}

.badge-gifted {
  background: #ddd6fe;
  color: #5b21b6;
}

.badge-free {
  background: #e5e7eb;
  color: #6b7280;
}

.badge-regular {
  background: #e5e7eb;
  color: #374151;
}

.badge-active {
  background: #d1fae5;
  color: #065f46;
}

.badge-deleted {
  background: #fee2e2;
  color: #991b1b;
}

.badge-verified {
  background: #d1fae5;
  color: #065f46;
}

.badge-unverified {
  background: #fef3c7;
  color: #92400e;
}

.email-verification-edit {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.verified-locked {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.lock-text {
  font-size: 0.75rem;
  color: #6b7280;
  font-style: italic;
}

.subscription-info {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.no-subscription {
  text-align: center;
  padding: 1rem;
}

.edit-input {
  padding: 0.375rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  width: 100%;
  max-width: 200px;
}

.edit-input:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.edit-textarea {
  padding: 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font-size: 0.875rem;
  width: 100%;
  resize: vertical;
  min-height: 100px;
  font-family: inherit;
  line-height: 1.4;
}

.edit-textarea:focus {
  outline: none;
  border-color: #3b82f6;
  box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.1);
}

.notes-row {
  flex-direction: column;
  align-items: stretch;
}

.notes-display {
  width: 100%;
}

.notes-content {
  margin: 0;
  padding: 0.75rem;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 0.375rem;
  white-space: pre-wrap;
  word-break: break-word;
  line-height: 1.4;
  color: #374151;
}

.no-notes {
  margin: 0;
  padding: 0.75rem;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 0.375rem;
  color: #9ca3af;
  font-style: italic;
  text-align: center;
}

.stripe-only {
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid #e5e7eb;
}

.edit-checkbox {
  width: auto;
  margin: 0;
}

.edit-select-container {
  display: flex;
  gap: 1rem;
}

.checkbox-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.875rem;
  cursor: pointer;
}

.premium-date-container {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  max-width: 280px;
}

.date-input {
  max-width: 100%;
}

.date-actions {
  display: flex;
  gap: 0.5rem;
}

.date-action-btn {
  padding: 0.375rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font-size: 0.75rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
}

.clear-btn {
  background: #f3f4f6;
  color: #374151;
}

.clear-btn:hover {
  background: #e5e7eb;
  border-color: #9ca3af;
}

.preset-btn {
  background: #3b82f6;
  color: white;
  border-color: #3b82f6;
}

.preset-btn:hover {
  background: #2563eb;
  border-color: #2563eb;
}

/* Mobile responsive */
@media (max-width: 768px) {
  .user-detail-overlay {
    padding: 0.5rem;
  }

  .user-detail-modal {
    max-height: 95vh;
  }

  .user-info-grid {
    grid-template-columns: 1fr;
    gap: 1rem;
  }

  .modal-header {
    padding: 1rem;
    flex-wrap: wrap;
    gap: 0.5rem;
  }

  .header-actions {
    gap: 0.25rem;
  }

  .modal-content {
    padding: 1rem;
  }

  .info-section {
    padding: 1rem;
  }

  .info-row {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.25rem;
  }

  .value {
    word-break: break-all;
  }

  .premium-date-container {
    max-width: 100%;
  }

  .date-actions {
    flex-direction: column;
  }

  .date-action-btn {
    width: 100%;
  }
}

/* Account Actions Styles */
.action-content {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.action-description h4 {
  margin: 0 0 0.5rem 0;
  color: #374151;
  font-size: 1rem;
}

.action-description p {
  margin: 0;
  color: #4b5563;
  font-size: 0.875rem;
  line-height: 1.4;
}

.action-btn {
  padding: 0.75rem 1.5rem;
  background: #3b82f6;
  color: white;
  border: none;
  border-radius: 0.375rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s;
  align-self: flex-start;
}

.action-btn:hover:not(:disabled) {
  background: #2563eb;
}

.action-btn:disabled {
  background: #9ca3af;
  cursor: not-allowed;
}

.reset-result {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.reset-result p {
  margin: 0;
  font-size: 0.875rem;
  font-weight: 500;
}

.reset-success {
  color: #065f46;
}

.reset-warning {
  color: #92400e;
}

.reset-link-label {
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
  font-size: 0.75rem;
  color: #6b7280;
}

.reset-link-input {
  padding: 0.375rem 0.75rem;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  font-size: 0.75rem;
  font-family: 'Courier New', monospace;
  width: 100%;
  background: #f9fafb;
}

.reset-modal-title {
  color: #1d4ed8 !important;
}

.reset-modal-note {
  color: #6b7280 !important;
  font-size: 0.875rem !important;
}

.confirm-reset-btn {
  padding: 0.5rem 1rem;
  background: #3b82f6;
  color: white;
  border: none;
  border-radius: 0.375rem;
  cursor: pointer;
  font-weight: 600;
}

.confirm-reset-btn:hover:not(:disabled) {
  background: #2563eb;
}

.confirm-reset-btn:disabled {
  background: #9ca3af;
  cursor: not-allowed;
}

/* Tabs */
.modal-tabs {
  display: flex;
  gap: 0.25rem;
  padding: 0 1.5rem;
  border-bottom: 1px solid #e5e7eb;
  flex-shrink: 0;
}

.tab-btn {
  padding: 0.75rem 1rem;
  background: none;
  border: none;
  border-bottom: 2px solid transparent;
  margin-bottom: -1px;
  cursor: pointer;
  font-size: 0.875rem;
  font-weight: 500;
  color: #6b7280;
  transition: all 0.2s;
}

.tab-btn:hover {
  color: #374151;
}

.tab-btn.active {
  color: #3b82f6;
  border-bottom-color: #3b82f6;
}

/* Bug Reports Tab (reuses the Emails table styles) */
.bug-report-row {
  cursor: pointer;
}

.bug-report-row:hover td {
  background: #f9fafb;
}

.bug-report-row .email-template {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  white-space: pre-wrap;
}

/* Emails Tab */
.emails-panel {
  width: 100%;
}

.emails-state {
  padding: 2rem;
  text-align: center;
  color: #6b7280;
}

.emails-error {
  color: #b91c1c;
}

.emails-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 0.875rem;
}

.emails-table th {
  text-align: left;
  padding: 0.5rem 0.75rem;
  border-bottom: 2px solid #e5e7eb;
  color: #374151;
  font-weight: 600;
}

.emails-table td {
  padding: 0.5rem 0.75rem;
  border-bottom: 1px solid #f3f4f6;
  color: #1f2937;
  vertical-align: top;
}

.email-subject {
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
}

.email-template {
  font-size: 0.75rem;
  color: #9ca3af;
}

.email-date {
  white-space: nowrap;
  color: #4b5563;
}

.email-badge {
  padding: 0.25rem 0.625rem;
  border-radius: 9999px;
  font-size: 0.7rem;
  font-weight: 600;
  text-transform: uppercase;
  white-space: nowrap;
}

.badge-delivered {
  background: #d1fae5;
  color: #065f46;
}

.badge-hard-bounce {
  background: #fee2e2;
  color: #991b1b;
}

.badge-soft-bounce {
  background: #fef3c7;
  color: #92400e;
}

.badge-sent {
  background: #e0e7ff;
  color: #3730a3;
}

/* Danger Zone Styles */
.danger-zone {
  border-color: #dc2626 !important;
  background: #fef2f2 !important;
}

.danger-zone h3 {
  color: #dc2626 !important;
  border-bottom-color: #dc2626 !important;
}

.danger-content {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.danger-description h4 {
  margin: 0 0 0.5rem 0;
  color: #dc2626;
  font-size: 1rem;
}

.danger-description p {
  margin: 0;
  color: #991b1b;
  font-size: 0.875rem;
  line-height: 1.4;
}

.danger-btn {
  padding: 0.75rem 1.5rem;
  background: #dc2626;
  color: white;
  border: none;
  border-radius: 0.375rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s;
  align-self: flex-start;
}

.danger-btn:hover:not(:disabled) {
  background: #b91c1c;
}

.danger-btn:disabled {
  background: #9ca3af;
  cursor: not-allowed;
}

/* Delete Confirmation Modal */
.delete-confirmation-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1100;
}

.delete-confirmation-modal {
  background: white;
  border-radius: 0.5rem;
  padding: 2rem;
  max-width: 400px;
  width: 90%;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
}

.delete-confirmation-modal h3 {
  margin: 0 0 1rem 0;
  color: #dc2626;
  font-size: 1.25rem;
}

.delete-confirmation-modal p {
  margin: 0 0 1rem 0;
  color: #374151;
  line-height: 1.5;
}

.warning-text {
  color: #dc2626 !important;
  font-weight: 500;
  font-size: 0.875rem !important;
}

.delete-confirmation-actions {
  display: flex;
  gap: 1rem;
  justify-content: flex-end;
  margin-top: 2rem;
}

.cancel-delete-btn {
  padding: 0.5rem 1rem;
  background: #f3f4f6;
  color: #374151;
  border: 1px solid #d1d5db;
  border-radius: 0.375rem;
  cursor: pointer;
  font-weight: 500;
}

.cancel-delete-btn:hover {
  background: #e5e7eb;
}

.confirm-delete-btn {
  padding: 0.5rem 1rem;
  background: #dc2626;
  color: white;
  border: none;
  border-radius: 0.375rem;
  cursor: pointer;
  font-weight: 600;
}

.confirm-delete-btn:hover:not(:disabled) {
  background: #b91c1c;
}

.confirm-delete-btn:disabled {
  background: #9ca3af;
  cursor: not-allowed;
}

/* Awards Section Styles */
.awards-container {
  width: 100%;
}

.awards-edit {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.award-edit-row {
  display: flex;
  gap: 0.5rem;
  align-items: center;
}

.award-icon-input {
  flex: 0 0 150px;
  max-width: 150px;
}

.award-desc-input {
  flex: 1;
}

.remove-award-btn {
  width: 2rem;
  height: 2rem;
  background: #ef4444;
  color: white;
  border: none;
  border-radius: 0.375rem;
  cursor: pointer;
  font-size: 1.25rem;
  line-height: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: background-color 0.2s;
}

.remove-award-btn:hover {
  background: #dc2626;
}

.add-award-btn {
  padding: 0.5rem 1rem;
  background: #3b82f6;
  color: white;
  border: none;
  border-radius: 0.375rem;
  cursor: pointer;
  font-size: 0.875rem;
  font-weight: 500;
  transition: background-color 0.2s;
  align-self: flex-start;
}

.add-award-btn:hover {
  background: #2563eb;
}

.awards-display {
  width: 100%;
}

.awards-list {
  display: flex;
  flex-direction: column;
  gap: 0.75rem;
}

.award-item {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.5rem;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 0.375rem;
}

.award-icon {
  font-size: 1.25rem;
  color: #f59e0b;
  flex-shrink: 0;
}

.award-description {
  color: #374151;
  font-size: 0.875rem;
}

.no-awards {
  margin: 0;
  padding: 0.75rem;
  background: #f9fafb;
  border: 1px solid #e5e7eb;
  border-radius: 0.375rem;
  color: #9ca3af;
  font-style: italic;
  text-align: center;
}
</style>
